import { EndpointData, Filters, Locale, PaginationType, RequestDataTypes } from './types'
import { SetStateAction } from 'react'
import type { MissingTranslation } from './components'
import request from '../../services/request'
import { message } from 'antd'

function decodeFilename(base64: string) {
    const binString = atob(base64)

    const bytes = Uint8Array.from(binString, (m) => m.charCodeAt(0))

    return new TextDecoder().decode(bytes)
}

export const downloadFiles = async (
    endpoints: EndpointData[],
    id: number,
    locale: Locale,
    setMissingTranslations: (value: SetStateAction<MissingTranslation[]>) => void,
    setTranslationModalVisible: (value: SetStateAction<boolean>) => void,
    toDecodeAll?: boolean,
): Promise<boolean> => {
    const anchors = [document.createElement('a'), document.createElement('a')]
    const objects = []
    const allMissing: string[] = []

    let operatorError = false

    anchors.forEach((anchor) => {
        anchor.style.display = 'none'
        document.body.appendChild(anchor)
    })

    for (const [
        index,
        { url, defaultExt, mime, withLogoParam, mainLogoId, isGradient, isA3, toDecode },
    ] of endpoints.entries()) {
        try {
            const params: Record<string, any> = { certificate_id: id, locale }
            if (withLogoParam) {
                params.client_logo = withLogoParam
            }
            if (mainLogoId) {
                params.main_logo_id = mainLogoId
            }
            if (isGradient) {
                params.is_gradient = isGradient
            }
            if (isA3) {
                params.is_a3 = isA3
            }
            const response = await request.get(url, {
                params: params,
                responseType: 'blob',
            })

            const decodeFlag = toDecode !== undefined ? toDecode : toDecodeAll
            const disposition = response.headers['content-disposition']
            const filenameMatch = disposition?.match(/filename="?([^"]+)"?/)
            let filename = filenameMatch?.[1] || `ownership_${id}.${defaultExt}`
            if (endpoints.length === 1 || decodeFlag) {
                filename = decodeFilename(filename)
            }

            const blob = new Blob([response.data], { type: mime })
            const objectUrl = window.URL.createObjectURL(blob)
            objects.push(objectUrl)

            anchors[index].href = objectUrl
            anchors[index].download = filename
        } catch (err: any) {
            const res = JSON.parse(await err.text())
            if (res?.detail?.user_errors?.length > 0 && locale === 'en') {
                try {
                    const text_data: string[] = await res.detail.user_errors
                    if (text_data) {
                        allMissing.push(...text_data)
                    }
                } catch (parseErr) {
                    console.error('Ошибка при парсинге списка строк без переводов:', parseErr)
                }
            } else if (res?.detail?.operator_errors?.length > 0 && locale === 'en') {
                operatorError = true
            }
        }
    }
    if (allMissing.length === 0) {
        if (operatorError) {
            message.error(
                'Данных для формирования Свидетельства на английском языке недостаточно' +
                    ', обратитесь к оператору с использованием раздела "Мои заявки"',
            )
        } else {
            anchors.forEach((a) => a.click())
        }
    } else {
        const allMissingUnique = [...new Set(allMissing)]
        setMissingTranslations(allMissingUnique.map((value) => ({ text: value })))
        setTranslationModalVisible(true)
        return false
    }
    objects.forEach((o) => window.URL.revokeObjectURL(o))
    anchors.forEach((a) => document.body.removeChild(a))
    return true
}

export const filtersToQuery = (
    queryParams: URLSearchParams,
    filters: Filters,
    curFilters?: Filters,
) => {
    curFilters = curFilters ? curFilters : filters
    if (curFilters) {
        if (curFilters.company_name) {
            queryParams.append('company_name', curFilters.company_name)
        }
        if (curFilters.number) {
            queryParams.append('number', curFilters.number)
        }
        if (curFilters.certificate_volume_start || curFilters.certificate_volume_start === 0) {
            queryParams.append(
                'certificate_volume_start',
                String(curFilters.certificate_volume_start),
            )
        }
        if (curFilters.certificate_volume_end || curFilters.certificate_volume_end === 0) {
            queryParams.append('certificate_volume_end', String(curFilters.certificate_volume_end))
        }
    }
    return queryParams
}

export const getData = (pag: PaginationType, fetchOwnership: (data: RequestDataTypes) => void) => {
    const { page, pageSize, filters } = pag
    let queryParams = new URLSearchParams({
        page_size: String(pageSize),
        page: String(page),
        ordering: '-created_at',
    })
    queryParams = filtersToQuery(queryParams, filters ?? {})

    fetchOwnership({
        addToLink: `?${queryParams.toString()}`,
    })
}

export const getFile = async (
    filters: Filters,
    filename: string,
    fetch: (data: RequestDataTypes) => void,
) => {
    let queryParams = new URLSearchParams({
        ordering: '-created_at',
    })
    queryParams = filtersToQuery(queryParams, filters)
    fetch({
        addToLink: `?${queryParams.toString()}`,
        onSuccess: (data) => {
            if (!data) {
                console.error('No csv file')
                return
            }
            const bom = new Uint8Array([0xef, 0xbb, 0xbf])
            const anchor = document.createElement('a')
            anchor.style.display = 'none'
            document.body.appendChild(anchor)
            const blob = new Blob([bom, data], { type: 'text/csv;charset=utf-8;' })
            const objectUrl = window.URL.createObjectURL(blob)

            anchor.href = objectUrl
            anchor.download = filename
            anchor.click()

            window.URL.revokeObjectURL(objectUrl)
            document.body.removeChild(anchor)
        },
    })
}
